import { DEFAULT_GC_TIME, DEFAULT_STALE_TIME } from '@/client/constants';
import type {
  AdminAuditParams,
  AdminAuditResponse,
  AdminEnvelope,
} from '@/client/types/admin/users-orgs';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import useGetQueryFn from '../use-get-query-fn';

export const adminAuditKeys = {
  all: ['admin', 'audit'] as const,
  list: (params: AdminAuditParams) => [...adminAuditKeys.all, params] as const,
};

/**
 * `GET /api/admin/users/audit` — unified admin audit trail.
 *
 * Despite living under `/users`, this covers organization and membership
 * actions too. Most-recent first. Pagination is `limit`/`skip`, not
 * `page`/`page_size` like the list endpoints.
 */
export const useAdminAuditQuery = (params: AdminAuditParams = {}) => {
  const queryFn = useGetQueryFn<AdminEnvelope<AdminAuditResponse>>({
    defaultErrorMessage: 'An unexpected error occurred while fetching the audit log',
    endpoint: '/api/admin/users/audit',
  });

  return useQuery({
    queryKey: adminAuditKeys.list(params),
    queryFn: () => queryFn({ limit: 100, skip: 0, ...params }),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
    select: (response) => response?.data,
  });
};
